import { useAuthState } from 'contexts/AuthProvider';
import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { UsedOutsideProviderError } from '@diritto/common/utils/exceptions';
import { useRequestSign } from 'ui-hooks/useRequestSign';
import {
  ClickAction,
  ClickTargetType,
  TrackerExtraInfo,
  TrackingEventType,
} from '@diritto/common/tracker/tracking-event.constant';
import { RollingReactionApi } from 'api/ApiHelper';
import { useTrackingState } from 'contexts/TrackingProvider';
import { RollingRelayDto } from '@diritto/common/relay/dtos/rolling-relay.dtos';
import { RollingReactionType } from '@diritto/common/rolling/constants';
import { RollingReactionDto } from '@diritto/common/rolling/dtos/rolling-reaction.dto';
import { configure } from 'utils/configure';
import { useShare } from 'ui-hooks/useShare';

type RollingReactionStateType = {
  count: number;
  isOn: boolean;
};

type StateType = {
  [RollingReactionType.Favorite]: RollingReactionStateType;
  [RollingReactionType.Recommend]: RollingReactionStateType;
  [RollingReactionType.Excited]: RollingReactionStateType;
  Share: RollingReactionStateType;
};

type ActionType = {
  action: () => void;
};

type ContextType = {
  [RollingReactionType.Favorite]: RollingReactionStateType & ActionType;
  [RollingReactionType.Recommend]: RollingReactionStateType & ActionType;
  [RollingReactionType.Excited]: RollingReactionStateType & ActionType;
  Share: RollingReactionStateType & ActionType;
};

const RollingReactionContext = createContext<ContextType>({
  [RollingReactionType.Favorite]: {
    count: 0,
    isOn: false,
    action: () => {},
  },
  [RollingReactionType.Recommend]: {
    count: 0,
    isOn: false,
    action: () => {},
  },
  [RollingReactionType.Excited]: {
    count: 0,
    isOn: false,
    action: () => {},
  },
  Share: {
    count: 0,
    isOn: false,
    action: () => {},
  },
});

export const RollingReactionProvider = ({
  rolling,
  rollingReaction,
  children,
  trackerExtraInfo,
}: {
  rolling: RollingRelayDto;
  rollingReaction?: RollingReactionDto;
  children?: ReactNode;
  trackerExtraInfo: TrackerExtraInfo;
}) => {
  const requestSign = useRequestSign();
  const { isLoggedIn } = useAuthState();
  const { tracker } = useTrackingState();
  const share = useShare();

  const [state, setState] = useState({
    [RollingReactionType.Favorite]: {
      count: rolling.counts.favoriteCount ?? 0,
      isOn: rollingReaction?.isFavorited ?? false,
    },
    [RollingReactionType.Recommend]: {
      count: rolling.counts.recommendCount ?? 0,
      isOn: rollingReaction?.isRecommended ?? false,
    },
    [RollingReactionType.Excited]: {
      count: rolling.counts.excitedCount ?? 0,
      isOn: rollingReaction?.isExcited ?? false,
    },
    Share: {
      count: rolling.counts.sharedCount ?? 0,
      isOn: rollingReaction?.isShared ?? false,
    },
  });

  const handleOnReaction = useCallback(
    async (rollingReactionType: RollingReactionType) => {
      tracker.track(TrackingEventType.Click, {
        clickType: ClickTargetType.Button,
        action: ClickAction.OnRollingReaction,
        trackerExtraInfo,
      });
      await RollingReactionApi.onReaction(rolling.id, rollingReactionType);
    },
    [rolling.id, tracker, trackerExtraInfo],
  );
  const handleOffReaction = useCallback(
    async (rollingReactionType: RollingReactionType) => {
      tracker.track(TrackingEventType.Click, {
        clickType: ClickTargetType.Button,
        action: ClickAction.OffRollingReaction,
        trackerExtraInfo,
      });
      await RollingReactionApi.offReaction(rolling.id, rollingReactionType);
    },
    [rolling.id, tracker, trackerExtraInfo],
  );

  const handleShare = useCallback(() => {
    tracker.track(TrackingEventType.Click, {
      clickType: ClickTargetType.Button,
      action: ClickAction.ShareRolling,
      trackerExtraInfo,
    });

    share({ title: rolling.title, url: `${configure.serviceUrl}/rollings/${rolling.id}` });

    if (isLoggedIn && !state.Share.isOn) {
      setState(prev => ({
        ...prev,
        Share: {
          ...prev.Share,
          isOn: true,
          count: prev.Share.count + 1,
        },
      }));
      RollingReactionApi.onShared(rolling.id).then();
    }
  }, [isLoggedIn, rolling.id, rolling.title, share, state.Share.isOn, tracker, trackerExtraInfo]);

  const resetState = useCallback((prev: StateType): StateType => {
    const reset = (rollingReactionState: RollingReactionStateType): RollingReactionStateType =>
      rollingReactionState.isOn ? { count: rollingReactionState.count - 1, isOn: false } : { ...rollingReactionState };

    return {
      [RollingReactionType.Favorite]: reset(prev[RollingReactionType.Favorite]),
      [RollingReactionType.Recommend]: reset(prev[RollingReactionType.Recommend]),
      [RollingReactionType.Excited]: reset(prev[RollingReactionType.Excited]),
      Share: prev.Share,
    };
  }, []);

  const on = useCallback(
    (rollingReactionType: RollingReactionType) => {
      setState(prev => ({
        ...resetState(prev),
        [rollingReactionType]: {
          ...prev[rollingReactionType],
          isOn: true,
          count: prev[rollingReactionType].count + 1,
        },
      }));
      handleOnReaction(rollingReactionType);
    },
    [handleOnReaction, resetState],
  );

  const off = useCallback(
    (rollingReactionType: RollingReactionType) => {
      setState(prev => ({
        ...resetState(prev),
        [rollingReactionType]: {
          ...prev[rollingReactionType],
          count: prev[rollingReactionType].count - 1,
          isOn: false,
        },
      }));
      handleOffReaction(rollingReactionType);
    },
    [handleOffReaction, resetState],
  );

  const handleToggle = useCallback(
    (rollingReactionType: RollingReactionType) => {
      if (!isLoggedIn) {
        requestSign();
        return;
      }

      const { isOn } = state[rollingReactionType];

      if (isOn) {
        off(rollingReactionType);
      } else {
        on(rollingReactionType);
      }
    },
    [isLoggedIn, state, requestSign, off, on],
  );

  const value = useMemo(
    () => ({
      [RollingReactionType.Favorite]: {
        ...state[RollingReactionType.Favorite],
        action: () => handleToggle(RollingReactionType.Favorite),
      },
      [RollingReactionType.Recommend]: {
        ...state[RollingReactionType.Recommend],
        action: () => handleToggle(RollingReactionType.Recommend),
      },
      [RollingReactionType.Excited]: {
        ...state[RollingReactionType.Excited],
        action: () => handleToggle(RollingReactionType.Excited),
      },
      Share: {
        ...state.Share,
        action: () => handleShare(),
      },
    }),
    [handleShare, state, handleToggle],
  );

  return <RollingReactionContext.Provider value={value}>{children}</RollingReactionContext.Provider>;
};

export const useRollingReaction = (rollingReactionType: RollingReactionType | 'Share') => {
  const context = useContext(RollingReactionContext);

  if (context === undefined) {
    throw new UsedOutsideProviderError('useOnOff was used outside of its Provider');
  }

  return context[rollingReactionType];
};
